import { useState } from 'react';
import { AuthLoginPortal } from './AuthLoginPortal';
import { AuthContactPage } from './AuthContactPage';
import { AuthOtherSignInPage } from './AuthRoleLoginPage';
import { AuthAssetOwnerPage } from './AuthAssetOwnerPage';
import { AuthForgotPasswordPage } from './AuthForgotPasswordPage';
import { RegisterTypeMenu } from '../regsiter/RegisterTypeMenu';
import { PageChrome } from '../components/PageChrome';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { ASSET_OWNER_LOGIN_TYPE } from '../login/authEntryPolicy';
import type { LoginOption } from '../login/loginConstants';
import styles from './AuthScreen.module.css';

export function AuthScreen() {
  const { t } = useI18n();
  const { authPane, openAuth, navigationBack } = useAppNavigation();
  const [passwordResetLoginType, setPasswordResetLoginType] =
    useState<LoginOption>('private client');
  const [passwordResetLockType, setPasswordResetLockType] = useState(false);
  const [passwordResetReturnPane, setPasswordResetReturnPane] = useState<
    'login' | 'login-asset-owner' | 'login-business-owner'
  >('login');

  const openForgotPassword = (
    loginType: LoginOption,
    lockType: boolean,
    returnPane: 'login' | 'login-asset-owner' | 'login-business-owner' = 'login-business-owner',
  ) => {
    setPasswordResetLoginType(loginType);
    setPasswordResetLockType(lockType);
    setPasswordResetReturnPane(returnPane);
    openAuth('forgot-password');
  };

  if (authPane === 'contact') {
    return (
      <div className={styles.authRoot}>
        <AuthContactPage onBack={() => navigationBack()} />
      </div>
    );
  }

  if (authPane === 'forgot-password') {
    return (
      <div className={styles.authRoot}>
        <AuthForgotPasswordPage
          key={`${passwordResetLoginType}-${passwordResetLockType ? 'locked' : 'open'}`}
          initialLoginType={passwordResetLoginType}
          allowLoginTypeChange={!passwordResetLockType}
          onBack={() => navigationBack()}
          onContact={() => openAuth('contact')}
          onBackToSignIn={() => openAuth(passwordResetReturnPane)}
        />
      </div>
    );
  }

  if (authPane === 'login-asset-owner') {
    return (
      <div className={styles.authRoot}>
        <AuthAssetOwnerPage
          onBack={() => openAuth('login')}
          onShowRegister={() => openAuth('register-asset-owner')}
          onContact={() => openAuth('contact')}
          onForgotPassword={() => openForgotPassword(ASSET_OWNER_LOGIN_TYPE, true)}
        />
      </div>
    );
  }

  if (authPane === 'register-asset-owner') {
    return (
      <div className={styles.authRoot}>
        <PageChrome
          title={t('auth.assetOwner.registerTitle')}
          onBack={() => openAuth('login-asset-owner')}
        >
          <div className={styles.registerMain}>
            <RegisterTypeMenu fixedRegisterOption="asset owner" />
          </div>
        </PageChrome>
      </div>
    );
  }

  if (authPane === 'login-business-owner') {
    return (
      <div className={styles.authRoot}>
        <AuthOtherSignInPage
          onBack={() => openAuth('login')}
          onContact={() => openAuth('contact')}
          onForgotPassword={(loginType) => openForgotPassword(loginType, false)}
        />
      </div>
    );
  }

  if (authPane === 'login') {
    return (
      <div className={styles.authRoot}>
        <AuthLoginPortal
          onShowOtherSignIn={() => openAuth('login-business-owner')}
          onShowRegister={() => openAuth('register')}
          onShowAssetOwner={() => openAuth('login-asset-owner')}
          onShowContact={() => openAuth('contact')}
          onForgotPassword={() => openForgotPassword('private client', true, 'login')}
        />
      </div>
    );
  }

  return (
    <div className={styles.authRoot}>
      <PageChrome title={t('auth.register')} onBack={() => navigationBack()}>
        <div className={styles.registerMain}>
          <p className={styles.registerPolicy}>{t('auth.register.policyIntro')}</p>
          <RegisterTypeMenu />
        </div>
      </PageChrome>
    </div>
  );
}
